const getInputType = (column) =>
  column.type === "lookup" ? "dropdown" : column.type;

export const parseNewDropdownValue = (value) => {
  const match = String(value ?? "").match(/^__new__(.*?)__(.*)$/);

  return match
    ? { fieldKey: match[1].trim(), label: match[2].trim() }
    : null;
};

export const getDropdownDisplayValue = (value) =>
  parseNewDropdownValue(value)?.label ?? value;

const getColumnLookup = (column, relationship) =>
  relationship?.options ??
  column.lookupOptions ??
  [];

const buildFieldConfig = (
  column,
  relationship = null
) => ({
  key: column.field,
  label: column.label,
  parentKey: "fields",
  type: column.type,

  lookup: getColumnLookup(column, relationship).map((option) => ({
    ...option,
    key:
      option.key === null || option.key === undefined
        ? ""
        : String(option.key)
  })),

  creatable: column.creatable === true,
  displayField: column.displayField ?? null,
  required: column.required === true,

  visibleWhenField: column.visibleWhenField ?? null,
  visibleWhenValue: column.visibleWhenValue ?? false,

  placeholder:
    column.type === "lookup"
      ? `Search ${column.label.toLowerCase()}...`
      : `Enter ${column.label.toLowerCase()}...`,
});

export const generateLabel = (
  values,
  labelConfiguration
) => {
  const parts = (
    labelConfiguration.labelSourceFields ?? []
  )
    .map((fieldName) => getDropdownDisplayValue(values[fieldName]))
    .filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
    );

  if (values.IsModification === true) {
    parts.push("Modification");
  }

  if (values.IsValue === true) {
    parts.push("Value");
  }

  return parts.join(
    labelConfiguration.labelSeparator ?? " | "
  );
};

export const buildMainEditConfig = ({
  config,
  columns,
  relationships,
}) => ({
  title: `Edit ${config.title}`,
  description: `Update the ${config.title?.toLowerCase()} details.`,
  displayLabel: `${config.title} Name`,
  displayValue: (row) =>
    row?.fields?.[config.labelField] ?? "",

  fields: columns
    .filter((column) => column.editable !== false)
    .filter((column) => column.field !== config.primaryKey)
    .map((column) => {
      const relationship = relationships.find(
        (item) => item.field === column.field
      );

      return buildFieldConfig(column, relationship);
    }),
});

export const buildLookupEditConfig = ({
  option,
  relationship,
  config,
  rows,
}) => {
  const fields = (relationship.targetColumns ?? [])
    .filter((column) => column.editable !== false)
    .map((column) => buildFieldConfig(column));

  if (option?.associatedItems != null) {
    const associatedEntityLabel = config.title ?? "Items";
    const associationLabel = `Associated ${associatedEntityLabel}`;

    fields.push({
      key: "AssociatedItems",
      label: associationLabel,
      relationshipLabel: associationLabel,
      associatedEntityLabel,
      type: "multiSelectDropdown",

      placeholder: `Search ${associatedEntityLabel.toLowerCase()}...`,

      lookup: (rows ?? []).map((row) => ({
        key: String(row.fields?.[config.primaryKey] ?? ""),
        label: row.fields?.[config.labelField] ?? ""
      })),

      selectedLookup: (option.associatedItems ?? []).map((item) =>
        String(item.key)
      )
    });
  }

  return {
    title: `Edit ${relationship.label}`,
    description: `Update the ${relationship.label?.toLowerCase()} details.`,
    displayLabel: relationship.label,
    displayValue: () => option?.label ?? "",
    fields
  };
};

export const buildAddConfig = ({
  entityConfig,
  relationship = null,
}) => {
  const isLookupEntity = Boolean(relationship);

  const source = isLookupEntity
    ? relationship
    : entityConfig;

  const entity = isLookupEntity
    ? relationship.targetEntity
    : entityConfig.entity;

  const tableName = isLookupEntity
    ? relationship.targetTable
    : entityConfig.tableName;

  const primaryKey = isLookupEntity
    ? relationship.targetKey
    : entityConfig.primaryKey;

  const labelField = isLookupEntity
    ? relationship.targetLabel
    : entityConfig.labelField;

  const title = isLookupEntity
    ? relationship.label
    : entityConfig.title;

  const sourceColumns = isLookupEntity
    ? relationship.targetColumns ?? []
    : entityConfig.columns ?? [];

  const autoGenerateLabel =
    source.autoGenerateLabel === true;

  const fields = sourceColumns
    .filter(
      (column) => column.editable !== false
    )
    .filter(
      (column) => column.field !== primaryKey
    )
    .filter(
      (column) =>
        autoGenerateLabel ||
        column.field !== labelField
    )
    .map((column) => buildFieldConfig(column));

  return {
    entity,
    tableName,
    primaryKey,
    labelField,

    title: `Add ${title}`,
    description: `Create a new ${title.toLowerCase()}.`,
    displayLabel: `${title} Name`,
    displayPlaceholder: `Enter ${title.toLowerCase()} name...`,

    fields,

    autoGenerateLabel: autoGenerateLabel
      ? (values) => generateLabel(values, source)
      : null,
  };
};
